// Real backend: the Recover Supabase project, protected by row-level security (trainees see only their rows).
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { SelectedMuscle } from '../lib/selection.ts';
import {
  CONSENT_VERSION, type Backend, type Checkin, type ConsentKind, type Profile, type Session, type Sharing,
  type TrainerLink, type TrainerNote, type Upcoming,
} from './types.ts';

type Row<T> = T & Record<string, unknown>;
const one = <T>(v: T | T[] | null): T | null => (Array.isArray(v) ? v[0] ?? null : v);

/** Throws on a PostgREST error; empty results come back as [] (or null for void RPCs). */
function check<T>(res: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (res.error) throw new Error(res.error.message);
  return (res.data ?? []) as NonNullable<T>;
}

/** Supabase Auth messages in plain words (the built-in sender allows only a few emails per hour). */
export function friendlyAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('rate limit') || m.includes('security purposes')) {
    return 'Too many sign-in emails were sent recently. Use the newest link already in your inbox, or try again in an hour.';
  }
  if (m.includes('expired') || m.includes('invalid')) return 'That code has expired or is wrong. Request a new sign-in email.';
  return message;
}

export class SupabaseBackend implements Backend {
  readonly isDemo = false;
  client: SupabaseClient;

  constructor(url: string, key: string, client?: SupabaseClient) {
    this.client = client ?? createClient(url, key, { auth: { persistSession: true, detectSessionInUrl: true, flowType: 'implicit' } });
  }

  async userId() {
    const { data } = await this.client.auth.getSession();
    return data.session?.user.id ?? null;
  }

  async email() {
    const { data } = await this.client.auth.getSession();
    return data.session?.user.email ?? null;
  }

  onAuthChange(cb: () => void) {
    const { data } = this.client.auth.onAuthStateChange(() => cb());
    return () => data.subscription.unsubscribe();
  }

  async sendCode(email: string) {
    const { error } = await this.client.auth.signInWithOtp({
      email, options: { shouldCreateUser: true, emailRedirectTo: window.location.origin },
    });
    if (error) throw new Error(friendlyAuthError(error.message));
  }

  async verifyCode(email: string, code: string) {
    const { error } = await this.client.auth.verifyOtp({ email, token: code, type: 'email' });
    if (error) throw new Error(friendlyAuthError(error.message));
  }

  async signOut() {
    await this.client.auth.signOut();
  }

  async profile(): Promise<Profile | null> {
    const id = await this.userId();
    if (!id) return null;
    const rows = check(await this.client.from('profiles').select('id, full_name, avatar, timezone, onboarded_at').eq('id', id).limit(1));
    return (rows[0] as Profile | undefined) ?? null;
  }

  async updateProfile(p: Partial<Omit<Profile, 'id'>>) {
    const id = await this.userId();
    check(await this.client.from('profiles').update(p).eq('id', id!));
  }

  async consents() {
    const rows = check(await this.client.from('consents').select('kind').is('revoked_at', null));
    return new Set(rows.map((r) => r.kind as ConsentKind));
  }

  async grantConsent(kind: ConsentKind) {
    if ((await this.consents()).has(kind)) return;
    check(await this.client.from('consents').insert({ kind, version: CONSENT_VERSION }));
  }

  async revokeConsent(kind: ConsentKind) {
    check(await this.client.from('consents').update({ revoked_at: new Date().toISOString() }).eq('kind', kind).is('revoked_at', null));
  }

  async sessions(since: Date): Promise<Session[]> {
    const rows = check(await this.client.from('sessions')
      .select('id, kind, activity_type, source, status, started_at, ended_at, intensity, recovery, note, session_muscles(muscle_id, selection)')
      .gte('started_at', since.toISOString())
      .order('started_at', { ascending: false }));
    return rows.map(({ session_muscles, ...s }) => ({ ...(s as Omit<Session, 'muscles'>), muscles: session_muscles as SelectedMuscle[] }));
  }

  async logSession(s: Session) {
    const { muscles, ...session } = s;
    check(await this.client.rpc('log_session', { p_session: session, p_muscles: muscles }));
  }

  async checkins(sinceDay: string): Promise<Checkin[]> {
    return check(await this.client.from('recovery_checkins').select('day, rating').gte('day', sinceDay).order('day', { ascending: false }));
  }

  async saveCheckin(c: Checkin) {
    check(await this.client.from('recovery_checkins').upsert(c, { onConflict: 'trainee_id,day' }));
  }

  async upcoming(): Promise<Upcoming[]> {
    return check(await this.client.from('upcoming_sessions').select('id, calendar_event_id, title, starts_at')
      .gt('starts_at', new Date().toISOString()).order('starts_at'));
  }

  async syncUpcoming(events: { calendar_event_id: string; title: string; starts_at: string }[]) {
    check(await this.client.rpc('sync_upcoming_sessions', { p_events: events }));
  }

  async notes(): Promise<TrainerNote[]> {
    const rows = check(await this.client.from('trainer_notes')
      .select('id, body, muscle_ids, raise_next_session, raised_at, created_at, trainers(full_name)')
      .order('created_at', { ascending: false }).limit(100));
    return rows.map(({ trainers, ...n }: Row<{ trainers: unknown }>) => ({
      ...(n as unknown as Omit<TrainerNote, 'trainer_name'>),
      trainer_name: one(trainers as { full_name: string } | null)?.full_name ?? 'Trainer',
    }));
  }

  async links(): Promise<TrainerLink[]> {
    const rows = check(await this.client.from('trainer_links')
      .select('id, share_muscles, share_ratings, share_solo, share_health, trainers(full_name)').eq('status', 'active'));
    return rows.map(({ trainers, ...l }: Row<{ trainers: unknown }>) => ({
      ...(l as unknown as Omit<TrainerLink, 'trainer_name'>),
      trainer_name: one(trainers as { full_name: string } | null)?.full_name ?? 'Trainer',
    }));
  }

  async updateSharing(linkId: string, sharing: Sharing) {
    check(await this.client.from('trainer_links').update(sharing).eq('id', linkId));
  }

  async revokeLink(linkId: string) {
    check(await this.client.rpc('revoke_trainer_link', { p_link: linkId }));
  }

  async createInvite(): Promise<string> {
    return check(await this.client.rpc('create_link_invite')) as string;
  }

  async deleteAccount() {
    check(await this.client.rpc('delete_my_account'));
    await this.client.auth.signOut();
  }
}
